import { useEffect, useMemo, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { cn } from "./ui";
import { ReportChart } from "./reports-chart";
import { api, apiErrorText, apiUrl } from "@/lib/api";
import { locale, t, tn } from "@/lib/i18n";

/* Живой отчёт «Откуда клиенты»: сколько контактов пришло с какой рекламы (площадка → кампания), из какого источника Zoho CRM
   и по какому каналу первого обращения — по дням, неделям или месяцам. Данные — GET /reports/ads (backend/hub/reports.py).
   Дата прихода: первое обращение, иначе первая сделка CRM, иначе создание карточки. */

type Report = {
  from: string;
  to: string;
  by: "day" | "week" | "month";
  filter: string[];
  seriesKind: "platform" | "campaign";
  allPlatforms: { name: string; count: number }[];
  total: number;
  withAds: number;
  noAds: number;
  basis: Record<string, number>;
  series: string[];
  periods: { start: string; total: number; counts: Record<string, number> }[];
  platforms: { name: string; count: number; campaigns: { name: string; count: number }[] }[];
  leadSources: { name: string; count: number }[];
  channels: { name: string; count: number }[];
};

const COLORS = ["bg-primary/80", "bg-sky-500", "bg-amber-500", "bg-violet-500", "bg-rose-500", "bg-teal-500"];
const OTHER = "bg-foreground/35";
const NONE = "bg-foreground/15";

const CHANNEL: Record<string, string> = {
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  email: t("почта"),
  wazzup: t("WhatsApp (архив Wazzup)"),
  instagram: "Instagram",
  "": t("не зафиксировано"),
};

const iso = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * 86_400_000);

type Preset = { id: string; label: string; from: () => string };
const today = () => new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()));
const PRESETS: Preset[] = [
  { id: "30", label: t("30 дней"), from: () => iso(addDays(today(), -29)) },
  { id: "90", label: t("90 дней"), from: () => iso(addDays(today(), -89)) },
  { id: "180", label: t("Полгода"), from: () => iso(addDays(today(), -182)) },
  { id: "year", label: t("Этот год"), from: () => `${today().getUTCFullYear()}-01-01` },
  { id: "365", label: t("12 месяцев"), from: () => iso(addDays(today(), -364)) },
  { id: "all", label: t("Всё время"), from: () => "2018-01-01" },
];

const pct = (v: number, total: number) => `${(total > 0 ? (v / total) * 100 : 0).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

function periodLabel(start: string, by: Report["by"], full = false): string {
  const d = new Date(`${start}T00:00:00Z`);
  if (by === "month") return d.toLocaleDateString(locale, { month: full ? "long" : "short", year: full ? "numeric" : "2-digit", timeZone: "UTC" });
  const day = d.toLocaleDateString(locale, { day: "numeric", month: "short", timeZone: "UTC" });
  if (by === "week") return full ? t("неделя с {day}", { day }) : day;
  return full ? d.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }) : day;
}

export default function ReportAds() {
  const [preset, setPreset] = useState("90");
  const [from, setFrom] = useState(PRESETS[1].from());
  const [to, setTo] = useState(iso(today()));
  const [by, setBy] = useState<"" | Report["by"]>("");
  const [rep, setRep] = useState<Report | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);

  const query = useMemo(() => {
    const q = new URLSearchParams({ from, to });
    if (by) q.set("by", by);
    for (const p of chosen) q.append("platform", p);
    return q.toString();
  }, [from, to, by, chosen]);
  useEffect(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to) || from > to) return;
    let alive = true;
    setBusy(true);
    setError("");
    api<Report>(`/reports/ads?${query}`)
      .then((r) => alive && setRep(r))
      .catch((e) => alive && setError(apiErrorText(e)))
      .finally(() => alive && setBusy(false));
    return () => {
      alive = false;
    };
  }, [query, from, to]);

  const togglePlatform = (name: string) => setChosen((prev) => (prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]));
  const platformLabel = (name: string) => (name === "none" ? t("без рекламных меток") : name);

  const pickPreset = (p: Preset) => {
    setPreset(p.id);
    setFrom(p.from());
    setTo(iso(today()));
  };

  const series = useMemo(() => {
    if (!rep) return [];
    const list = rep.series.map((name, j) => ({
      label: name === "__none__" ? t("кампания не указана") : name,
      tone: "neutral" as const,
      color: COLORS[j % COLORS.length],
      values: rep.periods.map((p) => p.counts[name] ?? 0),
    }));
    if (rep.periods.some((p) => p.counts.other)) {
      list.push({ label: rep.seriesKind === "campaign" ? t("другие кампании") : t("другие площадки"), tone: "neutral" as const, color: OTHER, values: rep.periods.map((p) => p.counts.other ?? 0) });
    }
    if (rep.periods.some((p) => p.counts.none)) list.push({ label: t("без рекламных меток"), tone: "neutral" as const, color: NONE, values: rep.periods.map((p) => p.counts.none ?? 0) });
    return list;
  }, [rep]);

  const labels = rep ? rep.periods.map((p) => periodLabel(p.start, rep.by)) : [];
  const fullLabels = rep ? rep.periods.map((p) => periodLabel(p.start, rep.by, true)) : [];
  const empty = !rep || rep.total === 0;
  const basisText = rep
    ? t("Дата прихода: первое обращение — {a}, первая сделка CRM — {b}, создание карточки — {c}.", {
        a: rep.basis.first ?? 0,
        b: rep.basis.deal ?? 0,
        c: rep.basis.created ?? 0,
      })
    : "";

  return (
    <div className="px-3.5 py-3" data-testid="report-ads">
      {/* период */}
      <div className="flex flex-wrap items-center gap-2 rounded-btn border border-border bg-background/40 px-3 py-2">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => pickPreset(p)}
            aria-pressed={preset === p.id}
            className={cn(
              "inline-flex h-7 cursor-pointer items-center whitespace-nowrap rounded-full border px-2.5 text-[10.5px] font-semibold transition",
              preset === p.id ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground",
            )}
          >
            {p.label}
          </button>
        ))}
        <label className="ms-1 inline-flex items-center gap-1 text-[10.5px] text-muted-foreground">
          {t("с")}
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => {
              setFrom(e.target.value);
              setPreset("");
            }}
            className="h-7 rounded-btn border border-border bg-card px-1.5 text-[11px] text-foreground"
            data-testid="report-from"
          />
        </label>
        <label className="inline-flex items-center gap-1 text-[10.5px] text-muted-foreground">
          {t("по")}
          <input
            type="date"
            value={to}
            min={from}
            onChange={(e) => {
              setTo(e.target.value);
              setPreset("");
            }}
            className="h-7 rounded-btn border border-border bg-card px-1.5 text-[11px] text-foreground"
            data-testid="report-to"
          />
        </label>
        <select
          value={by}
          onChange={(e) => setBy(e.target.value as "" | Report["by"])}
          aria-label={t("Детализация")}
          className="h-7 rounded-btn border border-border bg-card px-1.5 text-[11px] text-foreground"
          data-testid="report-by"
        >
          <option value="">{t("Авто")}</option>
          <option value="day">{t("По дням")}</option>
          <option value="week">{t("По неделям")}</option>
          <option value="month">{t("По месяцам")}</option>
        </select>
        <a
          href={apiUrl(`/reports/ads.csv?${query}`)}
          className="ms-auto inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn border border-border px-2.5 text-[10.5px] font-semibold text-foreground/80 transition hover:border-primary/50 hover:text-primary"
          data-testid="report-csv"
        >
          <Download className="size-3" aria-hidden />
          {t("Скачать CSV")}
        </a>
      </div>

      {error ? <p className="mt-2 text-[11.5px] text-red-500">{error}</p> : null}

      {/* фильтр по площадкам: чипы по всем площадкам периода, выбранные — в цвете; одна площадка — график по её кампаниям */}
      {rep && rep.allPlatforms.length ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="report-platforms">
          <span className="me-1 text-[10.5px] font-semibold text-muted-foreground">{t("Площадки:")}</span>
          <button
            type="button"
            onClick={() => setChosen([])}
            aria-pressed={!chosen.length}
            className={cn(
              "inline-flex h-6 cursor-pointer items-center rounded-full border px-2 text-[10.5px] font-semibold transition",
              !chosen.length ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground",
            )}
          >
            {t("Все")}
          </button>
          {rep.allPlatforms.map((p) => {
            const on = chosen.includes(p.name);
            return (
              <button
                key={p.name}
                type="button"
                onClick={() => togglePlatform(p.name)}
                aria-pressed={on}
                className={cn(
                  "inline-flex h-6 cursor-pointer items-center gap-1 rounded-full border px-2 text-[10.5px] transition",
                  on ? "border-primary bg-primary/10 font-semibold text-primary" : "border-border text-muted-foreground hover:text-foreground",
                )}
                data-testid="report-platform-chip"
              >
                {platformLabel(p.name)}
                <span className="tabular-nums opacity-70">{p.count.toLocaleString(locale)}</span>
              </button>
            );
          })}
          {chosen.length ? <span className="text-[10.5px] text-muted-foreground">{t("график и таблицы — только по выбранным")}</span> : null}
        </div>
      ) : null}

      {/* обзор и график */}
      <div className="mt-3 rounded-btn border border-border bg-card p-3.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h5 className="text-[12px] font-semibold">
            {rep?.seriesKind === "campaign" ? t("Новые контакты по кампаниям: {platform}", { platform: platformLabel(rep.filter[0] ?? "") }) : t("Новые контакты по рекламе")}
          </h5>
          {busy ? <Loader2 className="size-3.5 animate-spin text-muted-foreground" aria-hidden /> : null}
        </div>
        <div className="mt-2.5 flex flex-wrap gap-x-10 gap-y-3">
          <div>
            <p className="text-[11px] text-muted-foreground">{t("Контактов пришло")}</p>
            <p className="mt-1 text-[26px] font-semibold leading-none tabular-nums" data-testid="report-total">{(rep?.total ?? 0).toLocaleString(locale)}</p>
          </div>
          <div>
            <p className="text-[11px] text-muted-foreground">{t("С рекламы")}</p>
            <p className="mt-1 text-[26px] font-semibold leading-none tabular-nums">{(rep?.withAds ?? 0).toLocaleString(locale)}</p>
            <p className="mt-1 text-[10.5px] tabular-nums text-primary">{pct(rep?.withAds ?? 0, rep?.total ?? 0)}</p>
          </div>
          <div>
            <p className="text-[11px] text-muted-foreground">{t("Без рекламных меток")}</p>
            <p className="mt-1 text-[26px] font-semibold leading-none tabular-nums">{(rep?.noAds ?? 0).toLocaleString(locale)}</p>
            <p className="mt-1 text-[10.5px] tabular-nums text-muted-foreground">{pct(rep?.noAds ?? 0, rep?.total ?? 0)}</p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-border/60 pt-2">
          {series.map((s) => (
            <span key={s.label} className="inline-flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
              <span className={cn("size-2 flex-none rounded-sm", s.color)} aria-hidden />
              {s.label}
            </span>
          ))}
        </div>
        <div className="mt-3">
          <ReportChart labels={labels} fullLabels={fullLabels} series={series} empty={empty} emptyText={t("За выбранный период новых контактов нет.")} />
        </div>
        {rep ? <p className="mt-2 text-[10px] text-muted-foreground">{basisText}</p> : null}
      </div>

      {/* площадки и кампании */}
      <div className="mt-3 overflow-hidden rounded-btn border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3.5 py-2">
          <h5 className="text-[12px] font-semibold">{t("Площадки и кампании")}</h5>
          <span className="text-[10px] text-muted-foreground">{t("клик по площадке раскрывает кампании")}</span>
        </div>
        <table className="w-full border-collapse text-[11px]">
          <thead>
            <tr className="bg-background/40 text-[9.5px] uppercase tracking-wide text-muted-foreground">
              <th scope="col" className="px-3.5 py-1.5 text-start font-semibold">{t("Площадка / кампания")}</th>
              <th scope="col" className="px-2 py-1.5 text-end font-semibold">{t("Контактов")}</th>
              <th scope="col" className="px-3.5 py-1.5 text-end font-semibold">{t("Доля")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/70">
            {(rep?.platforms ?? []).map((p) => (
              <PlatformRows key={p.name} p={p} total={rep?.total ?? 0} open={open === p.name} onToggle={() => setOpen(open === p.name ? null : p.name)} />
            ))}
            {rep ? (
              <tr className="text-muted-foreground">
                <th scope="row" className="px-3.5 py-1.5 text-start font-medium">{t("Без рекламных меток")}</th>
                <td className="px-2 py-1.5 text-end tabular-nums">{rep.noAds.toLocaleString(locale)}</td>
                <td className="px-3.5 py-1.5 text-end tabular-nums">{pct(rep.noAds, rep.total)}</td>
              </tr>
            ) : null}
          </tbody>
          <tfoot>
            <tr className="border-t border-border bg-background/40 font-semibold">
              <td className="px-3.5 py-1.5">{t("Всего")}</td>
              <td className="px-2 py-1.5 text-end tabular-nums">{(rep?.total ?? 0).toLocaleString(locale)}</td>
              <td className="px-3.5 py-1.5 text-end tabular-nums">100%</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <SimpleTable title={t("Источник в Zoho CRM (Lead Source)")} rows={rep?.leadSources ?? []} total={rep?.total ?? 0} empty={t("не записан")} />
        <SimpleTable title={t("Канал первого обращения")} rows={(rep?.channels ?? []).map((r) => ({ name: CHANNEL[r.name] ?? r.name, count: r.count }))} total={rep?.total ?? 0} empty={t("не зафиксировано")} />
      </div>
      <p className="mt-2.5 text-[10.5px] text-muted-foreground">
        {t("Метки берутся из Zoho CRM (контакт, первая сделка, лид с тем же телефоном или почтой) — те же, что в блоке «Откуда клиент» карточки контакта.")}
      </p>
    </div>
  );
}

function PlatformRows({ p, total, open, onToggle }: { p: Report["platforms"][number]; total: number; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr className="cursor-pointer transition hover:bg-muted/50" onClick={onToggle} data-testid="report-platform">
        <th scope="row" className="px-3.5 py-1.5 text-start font-medium">
          <i className={cn("me-1 text-[11px] text-muted-foreground", open ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line")} aria-hidden />
          {p.name}
          <span className="ms-1.5 text-[10px] font-normal text-muted-foreground">{tn(p.campaigns.length, "кампания", "кампании", "кампаний")}</span>
        </th>
        <td className="px-2 py-1.5 text-end tabular-nums">{p.count.toLocaleString(locale)}</td>
        <td className="px-3.5 py-1.5 text-end tabular-nums">{pct(p.count, total)}</td>
      </tr>
      {open
        ? p.campaigns.map((c) => (
            <tr key={c.name || "-"} className="bg-background/30 text-muted-foreground">
              <td className="px-3.5 py-1 ps-8 text-start">{c.name || t("кампания не указана")}</td>
              <td className="px-2 py-1 text-end tabular-nums">{c.count.toLocaleString(locale)}</td>
              <td className="px-3.5 py-1 text-end tabular-nums">{pct(c.count, p.count)}</td>
            </tr>
          ))
        : null}
    </>
  );
}

function SimpleTable({ title, rows, total, empty }: { title: string; rows: { name: string; count: number }[]; total: number; empty: string }) {
  const [all, setAll] = useState(false);
  const shown = all ? rows : rows.slice(0, 8);
  return (
    <div className="overflow-hidden rounded-btn border border-border bg-card">
      <div className="border-b border-border px-3.5 py-2">
        <h5 className="text-[12px] font-semibold">{title}</h5>
      </div>
      <table className="w-full border-collapse text-[11px]">
        <tbody className="divide-y divide-border/70">
          {shown.map((r) => (
            <tr key={r.name || "-"}>
              <td className={cn("max-w-[14rem] truncate px-3.5 py-1.5", !r.name && "text-muted-foreground")}>{r.name || empty}</td>
              <td className="px-2 py-1.5 text-end tabular-nums">{r.count.toLocaleString(locale)}</td>
              <td className="px-3.5 py-1.5 text-end tabular-nums text-muted-foreground">{pct(r.count, total)}</td>
            </tr>
          ))}
          {!rows.length ? (
            <tr>
              <td className="px-3.5 py-2 text-muted-foreground" colSpan={3}>{t("Нет данных.")}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {rows.length > 8 ? (
        <button type="button" onClick={() => setAll((v) => !v)} className="w-full cursor-pointer border-t border-border px-3.5 py-1.5 text-start text-[10.5px] font-semibold text-primary hover:bg-muted/50">
          {all ? t("Свернуть") : t("Показать все ({n})", { n: rows.length })}
        </button>
      ) : null}
    </div>
  );
}
